import { TimeZone } from '@pelicargo/utils';
import { IATAFlightSegmentStatus } from '@prisma/client';

import { IATA } from '../iata';

export namespace AirCanada {
  // AC doesn't use booleans, they use yes/no. Gotta love enterprise
  export enum Bool {
    Y = 'Y',
    N = 'N',
  }

  export enum VolumeUnit {
    CubicMeters = 'MC',
    CubicFeet = 'CF',
    UnknownDF = 'DF', // I asked the CS team and they didn't know what it would mean in this case
  }

  // Spelling is important so I want it specified like VolumeUnit
  export enum WeightUnit {
    KG = 'KG',
    LB = 'LB',
  }
  export enum DimensionUnit {
    IN = 'IN',
    CM = 'CM',
  }

  export enum ParticipantType { // ChatGPT spat this out at Jeremy. Don't blame me :^)
    Airline = 'A', // cargo agent
    CargoAgent = 'C', // consignee
    Supplier = 'S',
    Reseller = 'R',
    Partner = 'P',
    Broker = 'B',
    NonProfit = 'N',
    FreightForwarder = 'F',
  }

  // AC-specific product codes
  export enum ProductCode {
    ADG = 'ADG',
    AFH = 'AFH',
    AFR = 'AFR',
    PDG = 'PDG',
    PFH = 'PFH',
    PPR = 'PPR',
    PPS = 'PPS',
    PPP = 'PPP',
    // PAB = 'PAB',
  }

  export type ShipmentAwbSubscriptionRequest = {
    events: string[]; // TODO: Figure out enum
    notificationFormat: 'basic' | null;
  };

  export type ErrorPlatformPolicy = {
    error: string;
  };

  export type SchedulesRequest = {
    origin: string;
    destination: string;
    date: string; // Format YYYY-MM-DD
    maxConnections: {
      number: number;
    };
  };

  type ErrorsWarning = {
    type: string; // TODO: Figure out enum
    code: string; // TODO: Figure out enum
    description: string;
  };
  export type ErrorsWarnings = {
    errors: ErrorsWarning[];
  };

  export type Dimension = {
    length: number;
    width: number;
    height: number;
    quantity: number;
    rotatable?: 'Y' | 'N';
  };

  export type TemperatureRequirements = {
    uncontrolledAmbientConditions: boolean;
    minTemperature: number;
    maxTemperature: number;
  };

  export type RequiredSpace = {
    pieces?: number;
    weight?: number;
    volume?: number;
    weightUnit?: 'KG' | 'LB';
    volumeUnit?: string; // TODO: Figure out enum
    dimensionUnit?: 'CM' | 'IN';
    dimensions?: Dimension[];
  };

  export type QuotesRequest = {
    origin: string;
    destination: string;
    product: ProductCode; // TODO: Figure out enum
    productOnly?: boolean;
    iataCassNumber?: string; // Example 093225
    shipmentDate: string; // Format YYYY-MM-DD
    includeFutureDays?: boolean;
    temperatureRequirements?: TemperatureRequirements;
    requiredSpace?: RequiredSpace;
    participantType?: ParticipantType; // TODO: Figure out enum
    handlingCodes?: IATA.HandlingCode[];
  };

  export type EAWBResponse = {
    eawbResponse: {
      businessHeader: {
        id: string;
        name: string;
        typeCode: number; // TODO: Figure out enum
        statusCode: string; // TODO: Figure out enum
      };
      responseStatus: {
        conditionCode: string; // TODO: Figure out enum
        reason: string;
      };
    };
  };

  export type Airport = {
    station: string; // Example: YYZ
    carrier?: string; // Example:
    timeZone?: TimeZone; // Example: America/Toronto
  };

  export type Routing = {
    origin: Airport;
    transfer?: Airport[];
    destination: Airport;
  };

  export type Allocation = {
    custAllocationId: string;
    product?: string; // TODO: Figure out enum
    category?: string; // TODO: Figure out enum
  };

  export type FlightInfo = {
    flight: string; // Example: 123
    date?: string; // Format YYYY-MM-DD
    departure?: {
      date?: string;
      time: string;
      status?: string; // TODO: Figure out enum
    };
    arrival?: {
      date?: string;
      time: string;
      status?: string; // TODO: Figure out enum
    };
    aircraft?: string; // Example: 777
    allocation?: {
      custAllocationId: string;
      product: string; // TODO: Figure out enum
      category: string; // TODO: Figure out enum
    }[];
    stops?: number;
    days?: string;
  };

  export type QuotingSegment = {
    origin: Airport;
    destination: Airport;
    flightInfo: FlightInfo;
    bookingStatus?: IATAFlightSegmentStatus; // TODO: Figure out enum
    requiredSpace?: RequiredSpace; // this is only for booking params
    aircraft?: string; // Example: 777
    allocation?: Allocation;
    custAllocationId?: string;
    space?: {
      weight: number;
      volume: number;
      ulds: {
        udp: number;
        ldp: number;
        ldc: number;
      };
    };
  };

  export type BookedSegment = {
    origin: {
      station: string;
    };
    destination: {
      station: string;
    };
    flightInfo: {
      flight: string;
      date: string;
    };
    bookingStatus: IATAFlightSegmentStatus;
    requiredSpace: {
      pieces: number;
      weight: number;
      volume: number;
    };
    totalRequiredSpace: {
      pieces: number;
      weight: number;
      volume: number;
    };
    aircraft: string;
  };

  export type ShipmentStatus = {
    statusDetail: {
      current: string;
      stationCode: string;
      pieces: number;
      weight: number;
      time: string;
      date: string; // Format YYYY-MM-DD
      stationName: string;
      offloadStation?: string;
      offloadStationName?: string;
      flight?: string;
      location?: string;
    };
    utc: {
      date: string; // Format YYYY-MM-DD
      time: string;
    };
  };

  export type ShipmentsResponse = {
    trackingResponse: {
      bookingInfo: {
        segments: QuotingSegment[];
      };
      shipmentDetails: ShipmentDetailsResponse;
      statusHistory: ShipmentStatus[];
    };
  };

  export type EAWBRequest = {
    eawbRequest: {
      bookingRequest: {
        messageHeader: {
          typeCode: number; // TODO: Figure out enum
          function: string; // TODO: Figure out enum
        };
        businessHeader: {
          id: string;
          includedHeaderNote: {
            contentCode: string; // TODO: Figure out enum
          }[];
          signatoryConsignorAuthentication: {
            signatory: string; // TODO: Figure out definition
          };
          signatoryCarrierAuthentication: {
            tualDateTime: string; // Format YYYY-MM-DDTHH:MM:SS
            signatory: string; // TODO: Figure out definition
            issueAuthenticationLocation: {
              name: string;
            };
          };
        };
        masterConsignment: {
          id: string;
          nilCarriageValueIndicator: boolean;
          declaredValueForCarriageAmountUnitCode: string; // Example: CAD
          declaredValueForCarriageAmount: number;
          nilCustomsValueIndicator: boolean;
          nilInsuranceValueIndicator: boolean;
          insuranceValueAmountCurrency: string;
          insuranceValueAmount: number;
          totalChargePrepaidIndicator: boolean;
          totalDisbursementPrepaidIndicator: boolean;
          associatedReferenceId: string;
          includedTareGrossWeightMeasure: number;
          includedTareGrossWeightMeasureUnitCode: string;
          grossVolumeMeasure: number;
          grossVolumeMeasureUnitCode: string;
          densityGroupCode: number;
          packageQuantity: number;
          unitLoadTransportEquipmentQuantity: number;
          totalPieceQuantity: number;
          productId: string; // TODO: Figure out enum
          consignorParty: {
            schemeAgencyId: string;
            primaryId: string;
            additionalId: string;
            name: string;
            countId: string;
            postalStructuredAddress: {
              postcodeCode: string;
              streetName: string;
              cityName: string;
              countryId: string;
              countryName: string;
              countrySubDivisionName: string;
              postOfficeBox: string;
              cityId: string;
              countrySubDivisionId: string;
            };
            definedTradeContact: {
              personName: string;
              directTelephoneCommunication: {
                completeNumber: number;
              };
            }[];
          };
          consigneeParty: {
            schemeAgencyID: string;
            primaryId: string;
            additionalId: string;
            name: string;
            countId: string;
            postalStructuredAddress: {
              postcodeCode: string;
              streetName: string;
              cityName: string; // TODO: Figure out enum
              countryId: string;
              countryName: string;
              countrySubDivisionName: string;
              postOfficeBox: string;
              cityId: string;
              countrySubDivisionId: string;
            };
            definedTradeContact: {
              personName: string;
              directTelephoneCommunication: {
                completeNumber: number;
              };
              uriEmailCommunication: {
                uriId: string;
              };
            };
          };
          freightForwarderParty: {
            primaryId: string;
            additionalId: string;
            name: string;
            countId: string;
            cargoAgentId: string;
            specifiedCargoAgentLocation: {
              id: string;
            };
            freightForwarderAddress: {
              postcodeCode: string;
              streetName: string;
              cityName: string;
              countryId: string;
              countryName: string;
              countrySubDivisionName: string;
              postOfficeBox: number;
              countrySubDivisionId: string;
            };
            definedTradeContact: {
              personName: string;
              directTelephoneCommunication: {
                completeNumber: number;
              };
            };
          };
          originLocation: {
            id: string;
            name: string;
          };
          finalDestinationLocation: {
            id: string;
            name: string;
          };
          specifiedLogisticsTransportMovement: {
            stageCode: string; // TODO: Figure out enum
            modeCode: number; // TODO: Figure out enum
            mode: string;
            id: string;
            sequenceNumber: number;
            usedLogisticsTransportMeans: {
              name: string;
            };
            arrivalEvent: {
              scheduledOccurrenceDateTime: string; // Format YYYY-MM-DDTHH:MM:SS
            };
            departureEvent: {
              scheduledOccurrenceDateTime: string; // Format YYYY-MM-DDTHH:MM:SS
              latestAcceptanceRelatedDateTime: string; // Format YYYY-MM-DDTHH:MM:SS
              occurrenceDepartureLocation: {
                id: string;
                name: string;
                typeCode: string;
              };
            };
          }[];
          includedAccountingNote: {
            contentCode: string; // TODO: Figure out enum
            content: string;
          };
          associatedReferenceDocument: {
            id: string;
            issueDateTime: string; // Format YYYY-MM-DDTHH:MM:SS
            typeCode: number; // TODO: Figure out enum
            name: string;
          }[];
          applicableLogisticsServiceCharge: {
            transportPaymentMethodCode: string; // TODO: Figure out enum
          };
          applicableOriginCurrencyExchange: {
            sourceCurrencyCode: string;
          };
          applicableDestinationCurrencyExchange: {
            conversionRate: number;
          };
          applicableRating: {
            typeCode: string; // TODO: Figure out enum
            totalChargeAmountCurrency: string;
            totalChargeAmount: number;
            consignmentItemQuantity: number;
            includedMasterConsignmentItem: {
              sequenceNumeric: string;
              grossWeightMeasure: number;
              grossWeightMeasureUnitCode: string; // TODO: Figure out enum. Example: KGM
              grossVolumeMeasure: number;
              grossVolumeMeasureUnitCode: string; // TODO: Figure out enum. Example: MTQ
              packageQuantity: number;
              pieceQuantity: number;
              information: string;
              natureIdentificationTransportCargo: {
                identification: string;
              };
              originCountry: {
                id: string;
              };
              applicableFreightRateServiceCharge: {
                categoryCode: string; // TODO: Figure out enum
                chargeableWeightMeasureUnitCode: string; // TODO: Figure out enum. Example: KGM
                chargeableWeightMeasure: number;
                appliedRate: number;
                appliedAmountUnitCode: string; // TODO: Figure out enum. Example: KGM
                appliedAmount: number;
              };
            }[];
          }[];
          applicableTotalRating: {
            typeCode: string; // TODO: Figure out enum
            applicableDestinationCurrencyServiceCharge: {
              collectAppliedAmount: number;
              collectAppliedAmountCurrency: string;
              destinationAppliedAmount: number;
              destinationAppliedAmountCurrency: string;
              totalAppliedAmount: number;
              totalAppliedAmountCurrency: string;
            };
            applicablePrepaidCollectMonetarySummation: {
              prepaidIndicator: boolean;
              weightChargeTotalAmount: number;
              valuationChargeTotalAmount: number;
              taxTotalAmount: number;
              agentTotalDuePayableAmount: number;
              carrierTotalDuePayableAmount: number;
              grandTotalAmount: number;
            }[];
          }[];
        };
      };
    };
  };

  export type DeleteBookingsRequest = {
    booking: {
      reasonForDeletion: string;
    };
  };

  export type Contact = {
    name?: string; // not required
    email?: string; // not required
    phone: string;
    address?: {
      addressLine?: string;
      station?: string;
      city?: string;
      state?: string;
      country?: string;
      postcode?: string;
    };
  };

  export type Participant = {
    name?: string;
    contact: Contact;
    accountNumber?: string;
    type: string; // TODO: Figure out enum
    iataCassNumber?: string; // undocumented
  };

  export type Booking = {
    dryIce: Bool; // TODO: Figure out enum. Example: Y
    otherRestrictedArticles: Bool; // TODO: Figure out enum. Example: N
    contactInfo?: {
      name: string;
      phoneNumber?: string;
      emailAddress: string;
    };
    weightUnit: 'KG' | 'LB';
    volumeUnit: VolumeUnit;
    dimensionUnit: 'CM' | 'IN';
    segments: BookedSegment[];
  };

  export type ParticipantInfo = {
    participants: Participant[];
  };

  export type Value = {
    declaredForInsurance: string; // TODO: Figure out enum
    declaredForCustoms: string; // TODO: Figure out enum
    insuranceAmount: string;
  };

  export type PackageDetailsResponse = {
    pieces: number;
    grossWeight: number;
    rateClass?: string; // TODO: Figure out enum

    /** @description the chargeable for the rate */
    chargeableWeight?: number;

    /** @description per-kg rate in dollars */
    rate?: number;

    /** @description total cost for air freight line item */
    totalWeightCharges?: number;

    description: string;
    stackable: boolean;
    rotatable: boolean;
  };

  export type UpdateBookingsRequest = {
    quoteId?: string;
    booking: Omit<Booking, 'segments'> & { segments: QuotingSegment[] };
    participantsInfo: ParticipantInfo;
    shipmentDetails: ShipmentDetailsRequest;
  };

  export type CreateBookingsResponse = {
    shipmentDetails: ShipmentDetailsResponse;
    booking: Booking;
    participantsInfo: ParticipantInfo;
  };

  export type RetrieveBookingsResponse = {
    shipmentDetails: ShipmentDetailsResponse;
    booking: Booking;
    participantsInfo: ParticipantInfo;
  };

  export type ShipmentDetailsRequest = {
    awb: string | 'NEW';
    prepaid: Bool;
    routing?: Routing;
    product?: string; // TODO: Figure out enum
    value?: Value;
    packageDetails: CreateBookingPackageDetails;
    dimensions?: Dimension[];
    handlingCodes?: IATA.HandlingCode[];
    temperatureRequirements?: TemperatureRequirements;
  };

  export type ShipmentDetailsResponse = {
    description: string;
    awb: string;
    exportRatingDetails: {
      paycode: string; // TODO: Figure out enum
    };
    handlingCodes: IATA.HandlingCode[]; // TODO: confirm the enum is correct/complete
    routing: Routing;
    /** @description also known as cutoff at */
    latestAcceptanceTime: string;
    timeOfPickup: string;
    createdAt: string;
    product: ProductCode; // TODO: Figure out enum
    value: Value;
    dimensions: Omit<Dimension, 'rotatable'>[];
    packageDetails: PackageDetailsResponse;
    otherCharges: {
      chargeCode: string; // TODO: Figure out enum
      chargeSubCode: string; // TODO: Figure out enum
      chargePaycode: string; // TODO: Figure out enum
      chargeAmount: number;
      chargeDescription: string;
    }[];
    /** @description total cost of the shipment / booking */
    totalCharge: number;
  };

  export type CreateBookingsRequest = {
    quoteId?: string;
    booking: Omit<Booking, 'segments'> & { segments: QuotingSegment[] };
    participantsInfo: ParticipantInfo;
    shipmentDetails: ShipmentDetailsRequest;
  };

  export type CreateBookingPackageDetails = {
    pieces: number;
    volume: number;
    grossWeight: number;
    description: string;
    weightUnit: WeightUnit;
    volumeUnit: VolumeUnit;
  };

  export type UpdateBookingsResponse = {
    shipmentDetails: ShipmentDetailsResponse;
    booking: Booking;
    participantsInfo: ParticipantInfo;
  };

  export type QuotesResponse = {
    ratesResponse: {
      weightUnit: 'KG' | 'LB';
      volumeUnitOfMeasure: VolumeUnit;
      countNotFound: boolean;
      option: {
        product: ProductCode;
        currency: string;
      };
      rates: {
        seqNo: number;
        quoteId: string;
        mileage: number;
        duration: string;
        segments: QuotingSegment[];
        product: {
          prdCode: ProductCode;
          latestAcceptedTime: {
            date: string;
            time: string;
          };
          timeOfAvailability: {
            date: string;
            time: string;
          };
          charges: {
            chrgWeight: number;
            weightChrg: number;
            otherRates: {
              charge: {
                code: string; // TODO: Figure out enum. Example: SC, SU, MY
                subcode: string; // TODO: Figure out enum. Example: B, OC, BD, D, O
                due: string; // TODO: Figure out enum
                amount: number;
                description: string;
              }[];
              totalOthers: number;
            };
          };
          price: number;
          totalBookingFees: number;
          rate: number;
          rateType: string; // TODO: Figure out enum (SR -> SPOT, NA -> TARIFF)
          rateTypeLabel: string;
          actualClass: string; // TODO: Figure out enum
          note: string;
        }[];
      }[];
    };
  };

  export type SchedulesResponse = {
    schedules: {
      seqNo: number;
      mileage: number;
      duration: number; // TODO: Figure out unit. In minutes?
      segments: QuotingSegment[];
    }[];
  };

  export type StatusResponse = {
    app: string;
    build: string;
    buildHash: string;
    buildDate: string; // Format YYYY-MM-DDTHH:MM:SS
    buildVersion: string;
  };

  // Taken from the original AC T&T code. Seems to still apply
  export const Statuses = {
    RCS: 'Goods Accepted',
    DEP: 'Flight Departed',
    TFD: 'Transferred Interline',
    MAN: 'Manifested (Assigned to Flight)',
    RCF: 'Checked-In',
    CIE: 'Checked-In Express',
    DLV: 'Delivered ',
    NFD: 'Customer Notified ',
    TRM: 'Transfer Manifested',
    BKD: 'Booked',
    INA: 'In Customs - Automatic',
    AIM: 'Authorized Inbound Movement',
    CHC: 'Customs Hold removed',
    RFF: 'Goods Removed From Flight',
    FBW: 'Forwarded to a Bonded Warehouse',
    FOH: 'Freight on Site',
    AHD: 'Airline Hold / Freight on Hand',
    RBK: 'Rebooked',
    OND: 'Gone On Delivery ',
    CRC: 'Reported to Customs',
    DOS: 'Delivered on Substitute Air Waybill',
    TXS: 'Transferred to a surface carrier',
    GND: 'Goods Not Delivered',
    CCD: 'Cleared Customs',
    PCD: 'Pieces decreased',
    PCI: 'Pieces Increased',
    ANS: 'Arrived in non-equipped station',
    RCT: 'Accepted Interline',
    AHR: 'Airline Hold Removed',
    GRE: 'Goods Returned',
    UED: 'ULD Emptied on Arrival',
    ULE: 'ULD Breakdown-Empty',
    FDC: 'Confirmed FDCA',
    UFD: 'Unconfirmed FDCA',
    FDR: 'FDCA Rejected',
    FDD: 'FDCA Increase',
    AFC: 'Automatic FDCA Confirmation',
    PCC: 'Part Cleared Customs',
    ACL: 'Cleared Customs - Automatic',
    ALX: 'Authorized Local Transfer',
    AWD: 'Documents Delivered to Customer',
    MSB: 'MSCA Balanced',
    SPI: 'Surface Pickup',
    LOC: 'Location Change',
    ULB: 'ULD Breakdown',
    FBA: 'FDCA Balanced',
    UNA: 'Unverified Arrival',
    DLM: 'Delivery Manifested',
    WGI: 'Weight Increase',
    WGD: 'Weight Decrease',
    MSC: 'Confirmed MSCA',
    SSC: 'Confirmed SSPD',
    OVC: 'Confirmed OVCD',
    MSU: 'Unconfirmed MSCA',
    SSU: 'Unconfirmed SSPD',
    OVU: 'Unconfirmed OVCD',
    MSR: 'MSCA Rejected',
    SSR: 'SSPD Rejected',
    OVR: 'OVCD Rejected',
    MSD: 'MSCA Decreased',
    SSD: 'SSPD Decreased',
    OVI: 'OVCD Increased',
    SSI: 'SPD Increased',
    RCD: 'Documents Received',
    RCG: 'Preliminary Goods Acceptance',
    RCC: 'Conditional Goods Acceptance',
    AWA: 'Document Available',
    FPS: 'Goods Stored',
    PRE: 'Prepared for Loading',
    DDL: 'Door Delivered',
    DIS: 'Discrepancy',
    AHL: 'Automatic Hold',
    AWR: 'Air waybill Registered',
    DOE: 'Documents Exported',
  };

  /**
   * Every AC endpoint we call, keyed the way {@link AuthenticatedClient} reads a type map: by method,
   * then by path relative to the gateway base URL. `{awb}` is filled from the call's `pathParams`.
   */
  export type Api = {
    GET: {
      '/cargo/bookings/{awb}': {
        request: { pathParams: { awb: string } };
        response: RetrieveBookingsResponse;
      };
      '/cargo/shipments/{awb}': {
        request: {
          pathParams: { awb: string };
          params: { bookinginfo: 'true' | 'false'; flightinfo: 'true' | 'false' };
        };
        response: ShipmentsResponse;
      };
    };
    POST: {
      '/cargo/quotes': { request: { body: QuotesRequest }; response: QuotesResponse };
      '/cargo/schedules': { request: { body: SchedulesRequest }; response: SchedulesResponse };
      '/cargo/bookings': { request: { body: CreateBookingsRequest }; response: CreateBookingsResponse };
    };
    PATCH: {
      '/cargo/bookings/{awb}': {
        request: { pathParams: { awb: string }; body: UpdateBookingsRequest };
        response: UpdateBookingsResponse;
      };
    };
    DELETE: {
      '/cargo/bookings/{awb}': {
        request: { pathParams: { awb: string }; body: DeleteBookingsRequest };
      };
    };
  };
}
